import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCart } from '../store/cart';
import { useSubmitBooking } from './useCatalogue';
import { money } from '../lib/format';
import { ProductThumb } from './ProductThumb';
import { EmptyState, Spinner } from '../components/ui';
import { useToast } from '../components/Toast';
import { ApiRequestError } from '../lib/api';

export function CheckoutPage() {
  const { qrToken } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { lines, total, clear } = useCart();
  const submit = useSubmitBooking();

  const [form, setForm] = useState({ name: '', mobile: '', email: '', companyName: '', notes: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});

  if (lines.length === 0) {
    return (
      <div className="p-6">
        <EmptyState
          title="Nothing to book yet"
          hint="Add a few pieces from the rack first."
          action={
            <button className="btn-outline" onClick={() => navigate(`/r/${qrToken}`)}>
              Back to collection
            </button>
          }
        />
      </div>
    );
  }

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = 'Please enter your name';
    if (!/^[0-9+\-\s()]{6,20}$/.test(form.mobile.trim())) e.mobile = 'Enter a valid phone number';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async () => {
    if (!validate()) return;
    try {
      const booking = await submit.mutateAsync({
        visitor: {
          name: form.name.trim(),
          mobile: form.mobile.trim(),
          email: form.email.trim() || undefined,
          companyName: form.companyName.trim() || undefined,
          notes: form.notes.trim() || undefined,
        },
        items: lines.map((l) => ({ assignmentId: l.assignmentId, quantity: l.quantity })),
      });
      clear();
      navigate(`/r/${qrToken}/confirmed/${booking.bookingNumber}`, { state: { booking } });
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.message : 'Could not place the booking.';
      toast(msg, 'error');
    }
  };

  return (
    <div className="space-y-5 px-4 pb-28 pt-4">
      <h1 className="text-lg font-semibold text-ink-900">Confirm your booking</h1>

      <section className="card divide-y divide-ink-100">
        {lines.map((l) => (
          <div key={l.assignmentId} className="flex items-center gap-3 p-3">
            <div className="h-12 w-12 shrink-0 overflow-hidden rounded-md bg-ink-50">
              <ProductThumb src={l.image} alt={l.name} label={l.photoId} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="line-clamp-1 text-sm font-medium">{l.name}</p>
              <p className="text-[11px] text-ink-400">
                Qty {l.quantity} · Rack {l.rackNumber}
              </p>
            </div>
            <span className="text-sm font-semibold text-gold-600">
              {l.price ? money(Number(l.price) * l.quantity) : '—'}
            </span>
          </div>
        ))}
        <div className="flex items-center justify-between p-3 text-sm">
          <span className="font-medium text-ink-600">Estimated total</span>
          <span className="text-base font-semibold">{money(total())}</span>
        </div>
      </section>

      <section className="space-y-3">
        <Field label="Full name *" error={errors.name}>
          <input
            className="input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            autoComplete="name"
          />
        </Field>
        <Field label="Mobile number *" error={errors.mobile}>
          <input
            className="input"
            inputMode="tel"
            value={form.mobile}
            onChange={(e) => setForm({ ...form, mobile: e.target.value })}
            autoComplete="tel"
          />
        </Field>
        <Field label="Email" error={errors.email}>
          <input
            className="input"
            inputMode="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            autoComplete="email"
          />
        </Field>
        <Field label="Company (optional)">
          <input
            className="input"
            value={form.companyName}
            onChange={(e) => setForm({ ...form, companyName: e.target.value })}
          />
        </Field>
        <Field label="Notes (optional)">
          <textarea
            className="input min-h-[72px]"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </Field>
      </section>

      <div className="fixed inset-x-0 bottom-0 z-30 mx-auto max-w-xl border-t border-ink-100 bg-white px-4 py-3">
        <button className="btn-gold w-full" onClick={onSubmit} disabled={submit.isPending}>
          {submit.isPending ? <Spinner className="h-4 w-4" /> : `Place booking · ${money(total())}`}
        </button>
      </div>
    </div>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="label">{label}</span>
      {children}
      {error && <span className="mt-1 block text-xs text-red-600">{error}</span>}
    </label>
  );
}
